import { TranslationWidth } from '@angular/common';
import { inject, Injectable, InjectionToken } from '@angular/core';
import { NgbDatepickerI18n, NgbDateStruct } from '@ng-bootstrap/ng-bootstrap';
import moment from 'moment';
import { MuiI18nProvider } from '../../../i18n';
import { ngbDateToMoment } from '../utility';


export abstract class MuiDatepickerI18n extends NgbDatepickerI18n {
  abstract getPlaceholderChars(): { year: string; month: string; day: string };
}

/**
 * Default implementation of the MUI datepicker i18n service. It provides translations to the NgbDatepicker component using
 * moment.js builtin locale information.
 */
@Injectable()
export class MuiDatepickerI18nDefault implements MuiDatepickerI18n {

  private _weekLabel = 'CW';
  private _placeholderChars: { year: string; month: string; day: string } = {
    year: 'Y',
    month: 'M',
    day: 'D'
  };
  private _muiI18nProvider = inject(MuiI18nProvider);

  constructor() {
    this.init();
  }

  getPlaceholderChars(): { year: string; month: string; day: string } {
    return this._placeholderChars;
  }

  getWeekdayLabel(weekday: number, width?: TranslationWidth): string {
    switch (width) {
      case TranslationWidth.Narrow:
        return moment.weekdaysMin(weekday);
      case TranslationWidth.Short:
      case TranslationWidth.Abbreviated:
        return moment.weekdaysShort(weekday);
      default:
        return moment.weekdays(weekday);
    }
  }

  getMonthShortName(month: number): string {
    return moment.monthsShort(month - 1);
  }

  getMonthFullName(month: number): string {
    return moment.months(month - 1);
  }

  getDayNumerals(date: NgbDateStruct): string {
    return date.day.toString();
  }

  getWeekNumerals(weekNumber: number): string {
    return weekNumber.toString();
  }

  getYearNumerals(year: number): string {
    return year.toString();
  }

  getMonthLabel(date: NgbDateStruct): string {
    return ngbDateToMoment({ ...date, month: date.month - 1 }).format('MMMM');
  }

  getWeekLabel(): string {
    return this._weekLabel;
  }

  getDayAriaLabel(date: NgbDateStruct): string {
    return ngbDateToMoment(date).format('LL');
  }

  private init(): void {
    // Get and update translations from the MUI i18n provider
    this._muiI18nProvider.datepicker().subscribe({
      next: translations => {
        this._weekLabel = translations.calendarWeekLabel;
        this._placeholderChars = translations.placeholderChars;
      }
    });
  }
}

export const MUI_DATEPICKER_I18N = new InjectionToken<MuiDatepickerI18n>('MUI_DATEPICKER_V2_I18N', {
  providedIn: 'root',
  factory: (): MuiDatepickerI18n => {
    return new MuiDatepickerI18nDefault();
  }
});
